/* eslint-disable no-param-reassign */
import React, { useEffect } from 'react';

import classes from './styles.module.scss';
import rewindIcon from '../../../../../assets/images/create-meeting/rewind.svg';
import playIcon from '../../../../../assets/images/create-meeting/play.svg';
import pauseIcon from '../../../../../assets/images/video-player/pause.svg';

export default function Playback({
  videoRef,
  isVideoPlaying,
  startTime,
  endTime,
  onTimeUpdate,
}) {

  const togglePlay = () => {
    if (!videoRef.current) {
      return;
    }

    // Use isVideoPlaying prop to determine state (more reliable than video.paused)
    if (!isVideoPlaying) {
      // Starting playback - ensure current time is within trim boundaries
      const video = videoRef.current;
      let { currentTime } = video;
      
      // Clamp to trim boundaries before playing
      if (currentTime < startTime) {
        currentTime = startTime;
        video.currentTime = currentTime;
      } else if (currentTime >= endTime) {
        currentTime = startTime;
        video.currentTime = currentTime;
      }
      
      // Notify parent of the clamped time
      if (onTimeUpdate) {
        onTimeUpdate(currentTime);
      }
      
      video.play().catch((error) => {
        console.error('Error playing video:', error);
      });
    } else {
      // Pausing playback
      videoRef.current.pause();
    }
  };

  const rewind = () => {
    if (!videoRef.current) {
      return;
    }

    const newTime = Math.max(startTime, videoRef.current.currentTime - 15);
    videoRef.current.currentTime = newTime;
    
    // Notify parent of the new time
    if (onTimeUpdate) {
      onTimeUpdate(newTime);
    }
    
    // If video was playing, continue playing after rewind
    if (isVideoPlaying) {
      videoRef.current.play().catch((error) => {
        console.error('Error playing video after rewind:', error);
      });
    }
  };

  const fastForward = () => {
    if (!videoRef.current) {
      return;
    }

    const newTime = Math.min(endTime - 0.1, videoRef.current.currentTime + 15); // Use endTime - 0.1 to avoid hitting exact boundary
    const clampedTime = Math.max(startTime, newTime); // Also ensure it's not below startTime
    videoRef.current.currentTime = clampedTime;

    // Notify parent of the new time
    if (onTimeUpdate) {
      onTimeUpdate(clampedTime);
    }

    // If we reached the end, pause
    if (clampedTime >= endTime - 0.1) {
      videoRef.current.pause();
      return;
    }

    // If video was playing, continue playing after fast forward
    if (isVideoPlaying) {
      videoRef.current.play().catch((error) => {
        console.error('Error playing video after fast forward:', error);
      });
    }
  };

  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement) {
      return undefined;
    }

    const updateTime = () => {
      const time = videoElement.currentTime;

      // Notify parent component of time update for playhead
      if (onTimeUpdate) {
        onTimeUpdate(time);
      }

      // Clamp to trim boundaries
      if (videoElement.currentTime < startTime) {
        videoElement.currentTime = startTime;
      }

      if (videoElement.currentTime >= endTime) {
        videoElement.currentTime = endTime;
        videoElement.pause();
      }
    };

      videoElement.addEventListener('timeupdate', updateTime);

    return () => {
        videoElement.removeEventListener('timeupdate', updateTime);
    };
  }, [endTime, startTime, videoRef, onTimeUpdate]);

  return (
    <div className={classes.Playback}>
      <button type="button" className={classes.button} onClick={rewind}>
        <img src={rewindIcon} alt="Rewind" />
      </button>
      <button 
        type="button" 
        className={`${classes.button} ${isVideoPlaying ? classes.pauseButton : ''}`}
        onClick={togglePlay}
      >
        <img src={isVideoPlaying ? pauseIcon : playIcon} alt={isVideoPlaying ? "Pause" : "Play"} />
      </button>
      <button type="button" className={classes.button} onClick={fastForward}>
        <img
          src={rewindIcon}
          alt="Fast forward"
          className={classes.fastForward}
        />
      </button>
    </div>
  );
}
